// src/encounter/lib/to-medication-result.ts
// Map a backend ProviderMedication (GET /api/Provider/GetMedication) → the DS
// MedicationResult the MedicationSearchWidget renders. name = the medication display
// (which carries the strength/form, e.g. "PARACETAMOL + CODEINE tablet 500 mg + 30 mg").
//
// Identity: `id` is the catalog `id` when present, else the `code` (the canonical,
// order-stable identifier — a real catalog hit always carries one). The positional
// `med-${index}` is a LAST RESORT only for the degenerate case where a hit has neither
// id nor code (a free-text/custom entry): it gives React a key but is NOT order-stable,
// so a re-sorted/paginated response could reassign it — acceptable because such rows
// can't be ordered against a code anyway. Prefer-code-over-index is the fix the #15
// review asked for; this comment documents the index assumption explicitly.
//
// `strength` is omitted (not mislabelled from unitOfMeasurement) — the display string
// already carries it. Structured strength/dose capture is deferred:
// TODO [PROD-1167]: map MedicationRequest.dosageInstruction once the A/P picker captures it.
import type { MedicationResult } from '@scripsteam/scrips-signal-ds';
import type { ProviderMedication } from '@/types/provider-api';

export function providerMedicationToResult(m: ProviderMedication, index: number): MedicationResult {
  const code = m.medicationCode?.code ?? undefined;
  const name = (m.medicationCode?.display ?? code ?? 'Medication').trim();
  const id = m.id ?? code ?? `med-${index}`;
  return { id, name, ...(code ? { code } : {}) };
}

/** One administration route valid for a medication (from the catalog row's `route[]`). */
export interface MedRoute {
  code: string;
  display: string;
  system?: string;
}

/** Coded search option — superset of the DS MedicationResult that ALSO carries the coding
 *  `system` (e.g. "DDC"). The IPS Medications add (PROD-1257) needs `code.system` for the
 *  CreateMedication body, which the lossy MedicationResult drops. Assignable to MedicationResult
 *  (extra fields) so it can also feed the A/P picker. PROD-1340: also carries the catalog's
 *  `route[]` + `unitOfMeasurement` so the dose dialog can pre-fill route/dose-unit on select. */
export interface MedicationOption {
  id: string;
  name: string;
  code: string;
  system: string;
  strength?: string;
  route?: MedRoute[];
  unitOfMeasurement?: string;
}

/** The picked medication as the dose dialog needs it — the DS result + the pre-fill fields. */
export type MedicationPick = MedicationResult & {
  /**
   * The coding system the search row or the saved order carried (e.g. "DDC"). Rides onto the
   * attached order's `medicationCode.system` (PROD-2831). Blank or absent when the source had none:
   * nothing guesses one.
   */
  system?: string;
  route?: MedRoute[];
  unitOfMeasurement?: string;
};

function toMedRoutes(routes: ProviderMedication['route']): MedRoute[] | undefined {
  const mapped = (routes ?? [])
    .filter((r): r is NonNullable<typeof r> => r != null && !!r.code)
    .map((r) => ({ code: r.code as string, display: r.display ?? (r.code as string), ...(r.system ? { system: r.system } : {}) }));
  return mapped.length > 0 ? mapped : undefined;
}

export function providerMedicationToOption(m: ProviderMedication, index: number): MedicationOption {
  const code = m.medicationCode?.code ?? '';
  const name = (m.medicationCode?.display ?? code ?? 'Medication').trim();
  return {
    id: m.id ?? code ?? `med-${index}`,
    name,
    code,
    system: m.medicationCode?.system ?? '',
    ...(toMedRoutes(m.route) ? { route: toMedRoutes(m.route) } : {}),
    ...(m.unitOfMeasurement ? { unitOfMeasurement: m.unitOfMeasurement } : {}),
  };
}

/**
 * Normalise whatever the rail dragged into a MedicationPick.
 *
 * REGRESSION FIX (PROD-1801): this used to be a bare `item as MedicationOption`, which held only while
 * the rail served medication SEARCH rows — those carry `name`. Since the rail became favourites-first
 * (A/P-17) the dragged row can be a CodeSearchResult, which carries `display` and no `name` at all —
 * so the dose dialog's header rendered blank. A cast cannot catch that; it is precisely what a cast
 * suppresses.
 *
 * Reads either shape and keeps every optional the dose dialog pre-fills from (route[],
 * unitOfMeasurement, strength) when the source happens to have them.
 */
export function toMedicationPick(item: unknown): MedicationPick {
  const r = item as {
    id?: string; code?: string; name?: string; display?: string; displayName?: string;
    strength?: string; system?: string; route?: MedicationPick['route']; unitOfMeasurement?: string;
  };
  const name = (r.name ?? r.display ?? r.displayName ?? '').trim();
  return {
    id: r.id ?? r.code ?? name,
    name,
    ...(r.code ? { code: r.code } : {}),
    ...(r.strength ? { strength: r.strength } : {}),
    ...(r.system ? { system: r.system } : {}),
    ...(r.route ? { route: r.route } : {}),
    ...(r.unitOfMeasurement ? { unitOfMeasurement: r.unitOfMeasurement } : {}),
  } as MedicationPick;
}
